import moment from "moment"

/**
 * Valeur par défaut DYNAMIQUE d'un filtre : « la plus récente ».
 *
 * Pourquoi ce module existe : le client demande que la page s'ouvre filtrée sur le plan de contrôle
 * **le plus récent**, tout en pouvant l'enlever. Le mécanisme de valeur par défaut existe
 * (`defaultValues` sur `dropdownList`, appliqué par `getInitialPageFilter`) mais il porte des
 * valeurs **littérales** : une valeur figée à l'installation désignerait encore l'ancien plan le
 * jour où un nouveau démarre, sans que rien ne le signale.
 *
 * ⚠️ On n'ordonne PAS les libellés. « Plan de contrôle 1 », « 2A », « niveau 2 » ne s'ordonnent pas
 * chronologiquement, et la liste déroulante les trie de surcroît alphabétiquement. La récence vient
 * d'une **date agrégée par valeur**, demandée au serveur.
 *
 * ⚠️ Et on ne trie pas côté serveur non plus. Une requête de liste déroulante porte déjà un
 * `groupByFieldUUIDs` sur son propre champ ; y ajouter un `ORDER BY` sur une date **non groupée**
 * est du SQL invalide. On demande donc `max(date)` par valeur — que le moteur agrège sans
 * difficulté — et on choisit ici. Cinq lignes à comparer, aucune raison de toucher au constructeur
 * SQL partagé par tous les dashboards.
 */

/**
 * Corps ETL qui rend, pour chaque valeur du champ filtré, la date la plus tardive qui l'accompagne.
 *
 * @param champUUID  le champ du filtre (ex. « Plan de contrôle »)
 * @param dateUUID   la date qui porte la récence (ex. « Fin de réalisation »)
 * @param cleValeur  alias de sortie pour la valeur
 * @param cleDate    alias de sortie pour la date agrégée
 */
export function corpsRecence(champUUID, dateUUID, cleValeur, cleDate) {
  return {
    selectAdvanced: [
      { fieldUUID: champUUID, outputUUID: cleValeur, aggFunction: "" },
      { fieldUUID: dateUUID, outputUUID: cleDate, aggFunction: "max" },
    ],
    groupByFieldUUIDs: [champUUID],
  }
}

/**
 * Même lecture que partout ailleurs dans le front : le moteur rend ses dates en `DD/MM/YYYY`.
 *
 * ⚠️ Comparer ces chaînes entre elles donnerait un ordre par JOUR puis par mois — « 29/09/2026 »
 * passerait devant « 01/10/2026 ». Il faut les parser.
 */
const enDate = valeur =>
  typeof valeur === "number" ? moment(valeur) : moment(valeur, ["DD-MM-YYYY"])

/**
 * La valeur la plus récente parmi les lignes rendues, ou `null`.
 *
 * ⚠️ Les valeurs VIDES sont écartées, et ce n'est pas cosmétique : sur le tenant CRC, des
 * occurrences n'ont aucun plan de contrôle et portent la date la plus tardive de toutes. Sans ce
 * filtrage, la valeur par défaut serait « aucun plan », c'est-à-dire un filtre qui ne montre rien.
 *
 * Rendre `null` plutôt qu'un pis-aller : l'appelant laisse alors le filtre vide, ce qui est le
 * comportement d'avant et non un écran faux.
 *
 * ⚠️ EX ÆQUO : la valeur la plus petite dans l'ordre alphabétique l'emporte. Ce n'est pas un détail
 * cosmétique. Sans départage, on garderait la première ligne rencontrée — or le moteur ne garantit
 * AUCUN ordre de sortie sans `ORDER BY`, et la requête n'en porte pas. Le défaut sauterait alors
 * d'un plan à l'autre entre deux ouvertures de page, sans que rien ne l'explique. La règle
 * alphabétique est arbitraire mais stable, et elle rejoint l'ordre dans lequel la liste déroulante
 * présente ses options : le défaut tombe sur le premier des ex æquo tels que l'utilisateur les voit.
 */
export function valeurLaPlusRecente(lignes, cleValeur, cleDate) {
  let meilleure = null
  let quand = null

  for (const ligne of Array.isArray(lignes) ? lignes : []) {
    const valeur = ligne?.[cleValeur]
    if (valeur === null || valeur === undefined || String(valeur).trim() === "") continue

    const date = enDate(ligne?.[cleDate])
    if (!date.isValid()) continue

    if (!quand) {
      quand = date
      meilleure = valeur
      continue
    }
    if (date.isAfter(quand)) {
      quand = date
      meilleure = valeur
      continue
    }
    // Même date : départage alphabétique, pour que le résultat ne dépende pas de l'ordre des lignes.
    if (date.isSame(quand) && String(valeur) < String(meilleure)) {
      meilleure = valeur
    }
  }

  return meilleure
}
